import { useMutation } from '@apollo/client';
import { useState } from 'react';
import ButtonComponent from '../../../components/UI/Button/Button.component';
import { ESCALATE_REPORT } from '../../../graphql/resolvers/moderation/escalateReport.gql';
import { OVERRIDE_REPORT } from '../../../graphql/resolvers/moderation/overrideReport.gql';
import { RESOLVE_POST_REPORTS } from '../../../graphql/resolvers/moderation/resolvePostReports.gql';
import { reasonToTier } from '../../../helpers/reasonToTier';
import VerdictTemplatePicker from './VerdictTemplatePicker';

type VerdictKind = 'DISMISS' | 'REWRITE' | 'REMOVE' | 'CONFIRM_NOTICE';

interface Report {
  id: string;
  status: string;
  reason: string;
  assignedModeratorId: string | null;
}

interface Props {
  reports: Report[];
  postId: string;
  currentUserRole: 'moderator' | 'admin';
  onResolved: () => void;
  disabled?: boolean;
}

export default function VerdictBar({
  reports,
  postId,
  currentUserRole,
  onResolved,
  disabled,
}: Props) {
  const [openVerdict, setOpenVerdict] = useState<VerdictKind | null>(null);
  const [escalating, setEscalating] = useState(false);
  const [escalateError, setEscalateError] = useState<string | null>(null);
  const [confirmingNotice, setConfirmingNotice] = useState(false);
  const [confirmNoticeError, setConfirmNoticeError] = useState<string | null>(
    null,
  );

  const allPending = reports.every((r) => r.status === 'PENDING');
  const allResolved =
    reports.length > 0 && reports.every((r) => r.status !== 'PENDING');
  const firstReason = reports[0]?.reason ?? null;
  const isNoticeTier =
    firstReason !== null && reasonToTier(firstReason) === 'notice';

  const [resolvePostReports] = useMutation(RESOLVE_POST_REPORTS, {
    refetchQueries: ['moderatorQueue'],
    awaitRefetchQueries: true,
  });
  const [escalateReport] = useMutation(ESCALATE_REPORT, {
    refetchQueries: ['moderatorQueue'],
    awaitRefetchQueries: true,
  });
  const [overrideReport] = useMutation(OVERRIDE_REPORT, {
    refetchQueries: ['moderatorQueue'],
    awaitRefetchQueries: true,
  });

  const handleConfirm = async (templateKey: string) => {
    if (!openVerdict) return;
    if (allPending) {
      await resolvePostReports({
        variables: { postId, verdict: openVerdict, templateKey },
      });
    } else if (allResolved && currentUserRole === 'admin') {
      await overrideReport({
        variables: {
          reportId: reports[0].id,
          verdict: openVerdict,
          templateKey,
        },
      });
    }
    setOpenVerdict(null);
    onResolved();
  };

  const handleConfirmNotice = async () => {
    setConfirmingNotice(true);
    setConfirmNoticeError(null);
    try {
      await resolvePostReports({
        variables: {
          postId,
          verdict: 'CONFIRM_NOTICE',
          templateKey: 'confirm-notice-default',
        },
      });
      onResolved();
    } catch (err: unknown) {
      setConfirmNoticeError(
        err instanceof Error ? err.message : 'Confirm notice failed',
      );
    } finally {
      setConfirmingNotice(false);
    }
  };

  const handleEscalate = async () => {
    setEscalating(true);
    setEscalateError(null);
    try {
      await escalateReport({ variables: { reportId: reports[0].id } });
      onResolved();
    } catch (err: unknown) {
      setEscalateError(err instanceof Error ? err.message : 'Escalate failed');
    } finally {
      setEscalating(false);
    }
  };

  if (allResolved && currentUserRole === 'admin') {
    const currentReport = reports[0];
    const STATUS_TO_VERDICT: Record<string, VerdictKind | null> = {
      DISMISSED: 'DISMISS',
      RESOLVED_REWRITE: 'REWRITE',
      RESOLVED_REMOVE: 'REMOVE',
      RESOLVED: null,
    };
    const currentVerdict =
      STATUS_TO_VERDICT[currentReport?.status ?? ''] ?? null;

    return (
      <div className="Moderation__VerdictBar Moderation__VerdictBar--override">
        {!openVerdict ? (
          <>
            <span className="Moderation__VerdictBar__OverrideLabel">
              Override to:
            </span>
            {currentVerdict !== 'DISMISS' && (
              <ButtonComponent
                variant="outline"
                size="sm"
                onClick={() => setOpenVerdict('DISMISS')}
                disabled={disabled}
              >
                Dismiss
              </ButtonComponent>
            )}
            {currentVerdict !== 'REWRITE' && (
              <ButtonComponent
                variant="outline"
                size="sm"
                onClick={() => setOpenVerdict('REWRITE')}
                disabled={disabled}
              >
                Request rewrite
              </ButtonComponent>
            )}
            {currentVerdict !== 'REMOVE' && (
              <ButtonComponent
                variant="danger"
                size="sm"
                onClick={() => setOpenVerdict('REMOVE')}
                disabled={disabled}
              >
                Remove post
              </ButtonComponent>
            )}
          </>
        ) : (
          <VerdictTemplatePicker
            verdict={openVerdict as 'DISMISS' | 'REWRITE' | 'REMOVE'}
            reason={firstReason}
            onConfirm={handleConfirm}
            onCancel={() => setOpenVerdict(null)}
          />
        )}
      </div>
    );
  }

  if (!allPending) return null;

  return (
    <div className="Moderation__VerdictBar">
      {!openVerdict ? (
        <>
          {isNoticeTier && (
            <ButtonComponent
              variant="outline"
              size="sm"
              onClick={handleConfirmNotice}
              disabled={disabled || escalating || confirmingNotice}
            >
              {confirmingNotice ? 'Confirming…' : 'Confirm notice'}
            </ButtonComponent>
          )}
          <ButtonComponent
            variant="outline"
            size="sm"
            onClick={() => setOpenVerdict('DISMISS')}
            disabled={disabled || escalating || confirmingNotice}
          >
            Dismiss all
          </ButtonComponent>
          <ButtonComponent
            variant="outline"
            size="sm"
            onClick={() => setOpenVerdict('REWRITE')}
            disabled={disabled || escalating || confirmingNotice}
          >
            Request rewrite
          </ButtonComponent>
          <ButtonComponent
            variant="danger"
            size="sm"
            onClick={() => setOpenVerdict('REMOVE')}
            disabled={disabled || escalating || confirmingNotice}
          >
            Remove post
          </ButtonComponent>
          <ButtonComponent
            variant="outline"
            size="sm"
            onClick={handleEscalate}
            disabled={disabled || escalating || confirmingNotice}
          >
            {escalating ? 'Escalating…' : 'Escalate'}
          </ButtonComponent>
          {escalateError && (
            <div className="Moderation__VerdictBar__Error">{escalateError}</div>
          )}
          {confirmNoticeError && (
            <div className="Moderation__VerdictBar__Error">
              {confirmNoticeError}
            </div>
          )}
        </>
      ) : (
        <VerdictTemplatePicker
          verdict={openVerdict as 'DISMISS' | 'REWRITE' | 'REMOVE'}
          reason={firstReason}
          onConfirm={handleConfirm}
          onCancel={() => setOpenVerdict(null)}
        />
      )}
    </div>
  );
}
